/* eslint-disable @typescript-eslint/array-type */
/**
 * Unified Model Catalog Service
 *
 * Combines chat models (shared-pricing-config) and generation models
 * (shared-generation-pricing-config) into a unified catalog for UI display.
 */

import {
  MODEL_PRICING,
  deriveCapabilitiesFromParams,
  familyToProvider,
} from '../../../../../shared-pricing-config'
import {
  computeMinGenerationPrice,
  getAllBaseModels,
  getBaseModelConfig,
  getModelGenerationTypes,
} from '../../../../../shared-generation-pricing-config'
import type { GenerationType } from '../../../../../shared-generation-pricing-config'
import type {
  ChatModelMetadata,
  ChatModelPricing,
  GenerationModelMetadata,
  GenerationModelPricing,
  SimpleGenerationType,
  UnifiedModel,
} from '@/features/models/types/unified-model.types'

/**
 * Map GenerationType to SimpleGenerationType
 */
function mapToSimpleGenerationType(genType: GenerationType | undefined): SimpleGenerationType {
  if (!genType) return 'image'
  if (genType.includes('IMAGE')) return 'image'
  if (genType.includes('VIDEO')) return 'video'
  if (genType.includes('AUDIO')) return 'audio'
  return 'image' // fallback
}

class ModelCatalogService {
  /**
   * Get all models (chat + generation) as unified models
   */
  getAllModels(): UnifiedModel[] {
    const chatModels = this.getChatModels()
    const generationModels = this.getGenerationModels()

    return [...chatModels, ...generationModels]
  }

  /**
   * Get only chat/text LLM models
   */
  getChatModels(): UnifiedModel[] {
    return Object.entries(MODEL_PRICING).map(([id, pricing]) => {
      const capabilities = deriveCapabilitiesFromParams(
        pricing.supportedParams,
        pricing.family,
      )

      const chatPricing: ChatModelPricing = {
        type: 'chat',
        inputPricePerMillion: pricing.inputPricePerMillion,
        outputPricePerMillion: pricing.outputPricePerMillion,
        cacheReadPricePerMillion: pricing.cacheReadPricePerMillion,
        cacheCreationPricePerMillion: pricing.cacheCreationPricePerMillion,
        cacheCreation1hPricePerMillion: pricing.cacheCreation1hPricePerMillion,
      }

      const chatMetadata: ChatModelMetadata = {
        maxContext: pricing.maxContext,
        maxOutput: pricing.maxOutput,
        maxContextTokens: pricing.maxContextTokens,
        latency: pricing.latency,
        releasedAt: pricing.releasedAt,
        capabilities,
      }

      return {
        id,
        name: pricing.displayName,
        provider: familyToProvider(pricing.family),
        family: pricing.family,
        type: 'chat' as const,
        description: pricing.description,
        strongestFor: pricing.strongestFor,
        featured: pricing.featured,
        icon: pricing.icon,
        version: pricing.version,
        dataCutoff: pricing.dataCutoff,
        pricing: chatPricing,
        metadata: chatMetadata,
      }
    })
  }

  /**
   * Get only generation models (image/video/audio)
   */
  getGenerationModels(): Array<UnifiedModel> {
    const results: Array<UnifiedModel> = []

    for (const baseModel of getAllBaseModels()) {
      const config = getBaseModelConfig(baseModel)
      if (!config) continue

      const types = getModelGenerationTypes(baseModel)

      for (const genType of types) {
        const variant = config.variants[genType]
        if (!variant) continue

        const simpleGenType = mapToSimpleGenerationType(genType)

        const genPricing: GenerationModelPricing = {
          type: 'generation',
          generationType: simpleGenType,
          priceMillidollars: computeMinGenerationPrice(baseModel, genType),
          priceUnit: variant.priceUnit ?? config.priceUnit,
        }

        const genMetadata: GenerationModelMetadata = {
          generationType: simpleGenType,
          supportedParams: variant.supportedParams,
        }

        results.push({
          id: `${baseModel}/${genType.toLowerCase()}`,
          name: config.displayName,
          provider: familyToProvider(config.family),
          family: config.family,
          type: 'generation' as const,
          description: variant.description,
          featured: variant.featured,
          icon: 'image',
          version: undefined,
          dataCutoff: undefined,
          pricing: genPricing,
          metadata: genMetadata,
        })
      }
    }

    return results
  }

}

export const modelCatalog = new ModelCatalogService()
